
import { supabase } from "@/integrations/supabase/client";
import type { Database } from '@/integrations/supabase/types';
import type { Stem } from '@/types/stem';

type StemSession = Database['public']['Tables']['stem_sessions']['Row'];
type StemSessionInsert = Database['public']['Tables']['stem_sessions']['Insert'];
export type SessionType = 'stem' | 'acapella' | 'drum';

const getSessionId = () => {
  let sessionId = localStorage.getItem('stem_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem('stem_session_id', sessionId);
  }
  return sessionId;
};

export const setSessionIdHeader = async () => {
  const sessionId = getSessionId();
  console.log('Setting session ID in header:', sessionId);
  
  try {
    supabase.auth.setSession({
      access_token: sessionId,
      refresh_token: ''
    });

    const { error: setError } = await supabase.rpc('set_claim', {
      claim: 'session_id',
      value: sessionId
    });
    
    if (setError) {
      console.error('Error setting session ID:', setError);
      throw setError;
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const { data: currentClaim, error: getError } = await supabase.rpc('get_claim', {
      claim_name: 'session_id'
    });
    
    console.log('Current session claim:', currentClaim);
    
    if (getError) {
      console.error('Error getting session claim:', getError);
      throw getError;
    }
    
    return sessionId;
  } catch (error) {
    console.error('Error in setSessionIdHeader:', error);
    throw error;
  }
};

export const fetchSessions = async (type: SessionType = 'stem') => {
  const sessionId = await setSessionIdHeader();
  console.log('Fetching sessions for session:', sessionId);
  
  const { data, error } = await supabase
    .from('stem_sessions')
    .select('*')
    .eq('session_id', sessionId)
    .eq('type', type)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching sessions:', error);
    throw error;
  }
  
  console.log('Fetched sessions:', data);
  return data as StemSession[];
};

export const createSession = async (
  sessionData: Omit<StemSessionInsert, 'session_id'> & { type: SessionType }
): Promise<StemSession> => {
  const sessionId = await setSessionIdHeader();
  console.log('Creating session with ID:', sessionId);
  
  const { data, error } = await supabase
    .from('stem_sessions')
    .insert([{ ...sessionData, session_id: sessionId }])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating session:', error);
    throw error;
  }
  
  console.log('Created session:', data);
  return data;
};

export const updateSession = async ({ 
  id, 
  stems, 
  status = 'completed',
  processing_minutes 
}: { 
  id: string; 
  stems: Stem[]; 
  status?: string;
  processing_minutes?: number;
}) => {
  console.log('Updating session:', { id, stems, status, processing_minutes });
  
  await setSessionIdHeader();
  
  const { error } = await supabase
    .from('stem_sessions')
    .update({ stems, status, processing_minutes })
    .eq('id', id);
  
  if (error) {
    console.error('Error updating session:', error);
    throw error;
  }
  
  console.log('Session updated successfully');
};

export const deleteSession = async (sessionId: string) => {
  console.log('Deleting session:', sessionId);
  
  await setSessionIdHeader();
  
  const { error } = await supabase
    .from('stem_sessions')
    .delete()
    .eq('id', sessionId);
  
  if (error) {
    console.error('Error deleting session:', error);
    throw error;
  }
  
  console.log('Session deleted successfully');
};

export const checkExistingSession = async (filename: string, type: SessionType) => {
  const sessionId = await setSessionIdHeader();
  console.log('Checking existing session for file:', filename);
  
  const { data, error } = await supabase
    .from('stem_sessions')
    .select('*')
    .eq('original_filename', filename)
    .eq('session_id', sessionId)
    .eq('type', type)
    .eq('status', 'processing')
    .is('stems', null)
    .maybeSingle();
  
  if (error) {
    console.error('Error checking existing session:', error);
    throw error;
  }
  
  console.log('Existing session check result:', data);
  return data;
};
